import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useUser } from "@clerk/clerk-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useSupabaseClient } from "@/lib/supabase";
import { isFeatureEnabled } from "@/lib/flags";
import { getLegalDocumentHashes } from "@/features/consent/legalDocuments";
import { legalAcceptanceClient } from "@/features/consent/client";

function AuthenticatedLegalAcceptance({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const { client, isReady } = useSupabaseClient();
  const legalClient = legalAcceptanceClient(client);
  const queryClient = useQueryClient();
  const [accepted, setAccepted] = useState(false);
  const [bridgeDelayed, setBridgeDelayed] = useState(false);
  const queryKey = ["legal-acceptance", user?.id] as const;

  useEffect(() => {
    setAccepted(false);
    setBridgeDelayed(false);
    if (isReady) return;
    const timer = window.setTimeout(() => setBridgeDelayed(true), 8_000);
    return () => window.clearTimeout(timer);
  }, [isReady, user?.id]);

  const status = useQuery({
    queryKey,
    enabled: isReady && Boolean(user?.id),
    retry: false,
    staleTime: 60_000,
    queryFn: async () => {
      const hashes = await getLegalDocumentHashes();
      const { data, error } = await legalClient.rpc("has_current_legal_acceptance", {
        p_terms_hash: hashes.terms,
        p_privacy_hash: hashes.privacy,
      });
      if (error) throw error;
      if (typeof data !== "boolean") throw new Error("invalid_legal_status");
      return data;
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      if (!accepted || !isReady || !user) throw new Error("legal_acceptance_required");
      const hashes = await getLegalDocumentHashes();
      const { data, error } = await legalClient.rpc("acknowledge_legal_documents", {
        p_terms_hash: hashes.terms,
        p_privacy_hash: hashes.privacy,
        p_accepted: accepted,
      });
      if (error) throw error;
      if (data !== true) throw new Error("legal_acceptance_not_saved");
    },
    onSuccess: () => queryClient.setQueryData(queryKey, true),
  });

  if (isReady && !status.isError && status.data === true) return <>{children}</>;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (accepted && !save.isPending) save.mutate();
  }

  const unavailable = status.isError || (!isReady && bridgeDelayed);
  return (
    <main className="min-h-screen bg-background px-4 py-10 flex items-center justify-center">
      <section className="w-full max-w-md rounded-xl border border-border bg-card p-6 space-y-5" aria-labelledby="legal-acceptance-title">
        <h1 id="legal-acceptance-title" className="text-xl font-semibold text-foreground">Termos e privacidade</h1>
        {unavailable ? (
          <>
            <p role="alert" className="text-sm text-destructive">Não foi possível conferir os documentos da sua conta. Recarregue a página para tentar novamente.</p>
            <Button onClick={() => window.location.reload()}>Recarregar página</Button>
          </>
        ) : status.isPending || !isReady ? (
          <p role="status" className="text-sm text-muted-foreground">Conferindo os documentos da sua conta…</p>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <p className="text-sm text-muted-foreground">Confirme os documentos atuais para continuar. A confirmação será registrada com a data e a versão apresentadas.</p>
            <div className="flex items-start gap-3">
              <input id="account-legal-acceptance" type="checkbox" checked={accepted}
                onChange={(event) => setAccepted(event.target.checked)} disabled={save.isPending}
                className="mt-1 h-5 w-5 shrink-0 accent-primary" required
                aria-describedby={save.isError ? "legal-save-error" : undefined} />
              <label htmlFor="account-legal-acceptance" className="text-sm leading-relaxed">
                Li e aceito os <Link to="/legal/termos" target="_blank" rel="noopener noreferrer" className="text-primary underline">Termos de Uso</Link>
                {" "}e declaro ciência da <Link to="/legal/privacidade" target="_blank" rel="noopener noreferrer" className="text-primary underline">Política de Privacidade</Link>.
              </label>
            </div>
            {save.isError && <p id="legal-save-error" role="alert" className="text-sm text-destructive">Não foi possível salvar sua confirmação. Tente novamente. Se o problema continuar, recarregue a página.</p>}
            <Button type="submit" disabled={!accepted || save.isPending} className="w-full">{save.isPending ? "Salvando…" : "Confirmar e continuar"}</Button>
          </form>
        )}
        <Link to="/meus-dados" className="inline-block text-sm text-primary underline">Acessar meus dados ou excluir minha conta</Link>
      </section>
    </main>
  );
}

export function LegalAcceptanceGate({ children }: { children: ReactNode }) {
  if (!isFeatureEnabled("AUTH_ENABLED")) return <>{children}</>;
  return <AuthenticatedLegalAcceptance>{children}</AuthenticatedLegalAcceptance>;
}
